import {
  ArrowLeft, Briefcase, CandlestickChart, Compass, Handshake, LineChart, Network,
  ShieldCheck, UserRound, Users, Wallet, Coins, type LucideIcon,
} from "lucide-react";

/**
 * Grouping for the dock. The nav trees (nav.ts, traderNav.ts, ibNav.ts,
 * managerNav.ts) stay flat lists of modules; the rail groups them under
 * quiet section labels so a 19-module back office reads as six areas
 * instead of one long scroll. Anything not listed falls into "More".
 */
export interface NavSection {
  /** Section label (shown as the panel title and the dock tooltip). */
  label: string | null;
  /** Dock icon for this section. */
  icon?: LucideIcon;
  /** Top-level nav labels, in the order they should appear. */
  items: string[];
}

export const ADMIN_SECTIONS: NavSection[] = [
  { label: "Overview", icon: Compass, items: ["Dashboard", "Notifications"] },
  { label: "Clients", icon: Users, items: ["Manage Trader", "KYC Management", "Manage Banks", "Support Tickets"] },
  { label: "Partners", icon: Handshake, items: ["IB Management", "Copy Trading", "Offers"] },
  { label: "Money", icon: Wallet, items: ["Manage Request", "Finance", "Fund Transfer"] },
  { label: "Trading", icon: CandlestickChart, items: ["Manage Trade", "Manage Accounts", "Group Settings"] },
  { label: "Insight", icon: LineChart, items: ["Reports"] },
  { label: "Administration", icon: ShieldCheck, items: ["Admin Profile", "Manage Admin", "Manager Settings", "Settings"] },
];

export const TRADER_SECTIONS: NavSection[] = [
  { label: "Overview", icon: Compass, items: ["Dashboard"] },
  { label: "Trading", icon: CandlestickChart, items: ["Trade Accounts", "Web Trader", "Trade History", "Account Settings"] },
  { label: "Money", icon: Wallet, items: ["Deposit Funds", "Withdraw Funds", "Fund Transfer", "Banks", "Bonus & Loyalty"] },
  { label: "Account", icon: UserRound, items: ["My Profile", "KYC Verification", "Become an IB", "Partner Area", "Support Tickets"] },
];

export const IB_SECTIONS: NavSection[] = [
  { label: "Overview", icon: Compass, items: ["Dashboard"] },
  { label: "Network", icon: Network, items: ["My Clients", "My IBs", "IB Tree", "Referral Links"] },
  { label: "Earnings", icon: Coins, items: ["Commissions", "Allot Commission", "Transfer to Wallet"] },
  { label: "Trader area", icon: ArrowLeft, items: ["Back to Trader Area"] },
];

export const MANAGER_SECTIONS: NavSection[] = [
  { label: "Overview", icon: Compass, items: ["Dashboard"] },
  { label: "Clients", icon: Users, items: ["My Clients", "My Team", "Activity Feed"] },
  { label: "Insight", icon: LineChart, items: ["Reports", "Referral Links"] },
  { label: "Profile", icon: UserRound, items: ["Profile"] },
];

/**
 * Order a flat nav list into sections. Items the caller's nav does not
 * include are skipped (module control can switch modules off), and items no
 * section mentions are appended under "More" so nothing disappears silently.
 */
export function groupNav<T extends { label: string }>(
  items: T[],
  sections: NavSection[],
): Array<{ label: string | null; icon?: LucideIcon; items: T[] }> {
  const byLabel = new Map(items.map((i) => [i.label, i]));
  const used = new Set<string>();
  const out: Array<{ label: string | null; icon?: LucideIcon; items: T[] }> = [];

  for (const section of sections) {
    const picked: T[] = [];
    for (const label of section.items) {
      const item = byLabel.get(label);
      if (item) {
        picked.push(item);
        used.add(label);
      }
    }
    if (picked.length) out.push({ label: section.label, icon: section.icon, items: picked });
  }

  const rest = items.filter((i) => !used.has(i.label));
  if (rest.length) out.push({ label: "More", icon: Briefcase, items: rest });
  return out;
}
